import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ScrollText, Check, Receipt } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { agencyMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { StatusPill, inr, LoadingScreen, EmptyState } from "@/components/common";

export default function AgencyContracts() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/contracts/agency").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const accept = async (c) => {
    try { await api.post(`/contracts/${c.id}/accept`); toast.success("Contract accepted"); load(); } catch (e) { toast.error("Failed"); }
  };
  const raiseInvoice = async (c) => {
    try { await api.post("/invoices", { contract_id: c.id }); toast.success("Invoice raised"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardShell menu={agencyMenu} title="Contracts">
      <PageHeader title="Your Contracts" subtitle="Accept new contracts and raise invoices." />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={ScrollText} title="No contracts yet" hint="Win contracts by submitting quotations to leads." />
      ) : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-3">Customer</th><th className="text-left p-3">Location</th><th className="p-3">Guards</th><th className="p-3">Monthly</th><th className="p-3">Duration</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-navy-100" data-testid={`ag-contract-${c.id}`}>
                  <td className="p-3 font-medium text-navy-900">{c.customer_name}</td>
                  <td className="p-3 text-navy-600">{c.service_location}</td>
                  <td className="p-3 text-center">{c.num_guards}</td>
                  <td className="p-3 text-center font-semibold">{inr(c.monthly_charges)}</td>
                  <td className="p-3 text-center text-navy-600">{c.contract_duration_months}mo</td>
                  <td className="p-3 text-center"><StatusPill status={c.status} /></td>
                  <td className="p-3 text-right space-x-2 whitespace-nowrap">
                    {!c.agency_accepted && c.status === "PENDING_ACCEPTANCE" && <Button size="sm" className="bg-brand hover:bg-brand-600" onClick={() => accept(c)} data-testid={`accept-ag-${c.id}`}><Check size={14} className="mr-1" /> Accept</Button>}
                    {c.status === "ACTIVE" && <Button size="sm" variant="outline" className="border-navy-300" onClick={() => raiseInvoice(c)} data-testid={`invoice-ag-${c.id}`}><Receipt size={14} className="mr-1" /> Invoice</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
